import React from "react";

import "./statement-settings-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsTabs from "../kit/ToolsTabs";
import ToolsCheckbox from "../kit/ToolsCheckbox";

const TABS = [
	{ key: "statements", label: "Statements" },
	{ key: "report", label: "Compilation report" },
	{ key: "accounts", label: "Accounts" },
];

class StatementSettingsModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			activeTab: "statements",
			roundingDraftCode: props.roundingCanonicalCode || null,
			savingASetting: false,
			saveFailureReason: null,
			entityNameDraft: props.statementEntityName || "",
			dropBracketsDraft: !!props.dropOwnerNameBrackets,
			signatoryCountDraft: props.signatoryCount || 1,
			signatoryTitleDraft: props.signatoryTitle || "Director",
			jurisdictionDraft: props.jurisdiction || "",
			incorporationDateDraft: props.incorporationDate || "",
			principalActivitiesDraft: props.principalActivities || "",
			compilationReportCityDraft: props.compilationReportCity || "",
			compilationReportDateDraft: props.compilationReportDate || "",
		};
	}

	renderEffectiveValue = ({ label, value, absentNote }) => {
		if (!value && !absentNote) return null;
		if (!value) return <p className="stmt-settings__current">{absentNote}</p>;

		return (
			<p className="stmt-settings__current">
				{label} <strong>{value}</strong>
			</p>
		);
	};

	savePresentationSettings = async settings => {
		const { clientId, wpfEngagementId, onPresentationSettingsChange } = this.props;

		await this.update({ savingASetting: true, saveFailureReason: null });
		try {
			const result = await BookkeepingService.setEngagementPresentationSettings({ clientId, wpfEngagementId, settings });
			await this.update({ savingASetting: false });
			if (onPresentationSettingsChange) onPresentationSettingsChange(result || {});
		} catch (error) {
			await this.update({ savingASetting: false, saveFailureReason: "That could not be saved." });
		}
	};

	onSaveRoundingAccount = async () => {
		await this.savePresentationSettings({ roundingCanonicalCode: this.state.roundingDraftCode });
	};

	onClearRoundingAccount = async () => {
		await this.update({ roundingDraftCode: null });
		await this.savePresentationSettings({ roundingCanonicalCode: null });
	};

	onSaveBusinessDescription = async () => {
		await this.savePresentationSettings({
			jurisdiction: this.state.jurisdictionDraft.trim(),
			incorporationDate: this.state.incorporationDateDraft,
			principalActivities: this.state.principalActivitiesDraft.trim(),
		});
	};

	onSaveApprovalBlock = async () => {
		await this.savePresentationSettings({
			signatoryCount: Number(this.state.signatoryCountDraft),
			signatoryTitle: this.state.signatoryTitleDraft.trim() || "Director",
		});
	};

	onSaveCompilationReport = async () => {
		await this.savePresentationSettings({
			compilationReportCity: this.state.compilationReportCityDraft.trim(),
			compilationReportDate: this.state.compilationReportDateDraft,
		});
	};

	onSaveTitlePage = async () => {
		await this.savePresentationSettings({
			statementEntityName: this.state.entityNameDraft.trim(),
			dropOwnerNameBrackets: this.state.dropBracketsDraft,
		});
	};

	renderApply = ({ label, testId, onClick, unchanged }) => (
		<div className="stmt-settings__actions">
			<button
				type="button"
				className="tk-button tk-button--secondary"
				data-test={testId}
				onClick={onClick}
				disabled={this.state.savingASetting || unchanged}
			>
				{this.state.savingASetting ? "Saving…" : label}
			</button>
		</div>
	);

	renderNameOnStatements = () => {
		const { statementEntityName, dropOwnerNameBrackets, clientName } = this.props;
		const { entityNameDraft, dropBracketsDraft } = this.state;

		const unchanged = entityNameDraft.trim() === (statementEntityName || "")
			&& dropBracketsDraft === !!dropOwnerNameBrackets;

		return (
			<section className="stmt-settings__group">
				<h4 className="stmt-settings__group-title">Title page</h4>
				<p className="stmt-settings__hint">
					The statements are headed with the client's name as it appears on their record. Override it here if
					they report under an operating name, or drop the owner's name where workflow has appended it in
					brackets.
				</p>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Name on the statements</label>
					<input
						className="tk-input"
						data-test="statement-entity-name"
						value={entityNameDraft}
						placeholder={clientName || "The name on the client record"}
						onChange={e => this.update({ entityNameDraft: e.target.value })}
						spellCheck={true}
					/>
					<p className="tk-form-help">Leave blank to use the name on the client record.</p>
				</div>

				<ToolsCheckbox
					containerClass="stmt-settings__checkbox"
					size="form"
					label="Drop the owner's name where it appears in brackets at the end"
					checked={dropBracketsDraft}
					onChange={() => this.update({ dropBracketsDraft: !dropBracketsDraft })}
				/>

				{this.renderApply({ label: "Apply to the title page", testId: "apply-title-page", onClick: this.onSaveTitlePage, unchanged })}
			</section>
		);
	};

	renderBusinessDescription = () => {
		const { jurisdiction, incorporationDate, principalActivities, clientProvince } = this.props;
		const { jurisdictionDraft, incorporationDateDraft, principalActivitiesDraft } = this.state;

		const unchanged = jurisdictionDraft.trim() === (jurisdiction || "")
			&& incorporationDateDraft === (incorporationDate || "")
			&& principalActivitiesDraft.trim() === (principalActivities || "");

		return (
			<section className="stmt-settings__group">
				<h4 className="stmt-settings__group-title">Description of business note</h4>
				<p className="stmt-settings__hint">
					The description of business note says the same three things for every client. Record them once and
					the sentence writes itself, and carries into next year. Anything you type into the note itself
					always wins over this.
				</p>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Incorporated under the laws of</label>
					<input
						className="tk-input"
						data-test="jurisdiction"
						value={jurisdictionDraft}
						placeholder={clientProvince || "Ontario"}
						onChange={e => this.update({ jurisdictionDraft: e.target.value })}
						spellCheck={true}
					/>
					{!jurisdictionDraft.trim() && clientProvince && (
						<p className="stmt-settings__current">
							The client record says <strong>{clientProvince}</strong>. It is offered rather than assumed,
							because this sentence is printed on a note the client reads.
						</p>
					)}
				</div>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Date of incorporation</label>
					<input
						className="tk-input"
						data-test="incorporation-date"
						type="date"
						value={incorporationDateDraft}
						onChange={e => this.update({ incorporationDateDraft: e.target.value })}
					/>
				</div>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Principal business activity</label>
					<input
						className="tk-input"
						data-test="principal-activities"
						value={principalActivitiesDraft}
						placeholder="the operation of a retail bakery"
						onChange={e => this.update({ principalActivitiesDraft: e.target.value })}
						spellCheck={true}
					/>
					<p className="tk-form-help">Finishes the sentence "Its principal business activity is …".</p>
				</div>

				{this.renderApply({ label: "Apply business description", testId: "apply-business-description", onClick: this.onSaveBusinessDescription, unchanged })}
			</section>
		);
	};

	renderApprovalBlock = () => {
		const { signatoryCount, signatoryTitle } = this.props;
		const { signatoryCountDraft, signatoryTitleDraft } = this.state;

		const unchanged = Number(signatoryCountDraft) === Number(signatoryCount || 1)
			&& signatoryTitleDraft.trim() === (signatoryTitle || "Director");

		return (
			<section className="stmt-settings__group">
				<h4 className="stmt-settings__group-title">Who approves the balance sheet</h4>
				<p className="stmt-settings__hint">
					The balance sheet is approved by the people responsible for it. One signs in their own right; two or
					more sign on behalf of the board, which is different wording and needs a line each.
				</p>

				<div className="stmt-settings__field">
					<label className="tk-form-label">People approving the balance sheet</label>
					<ToolsSelect
						value={signatoryCountDraft}
						options={[{ value: 1, label: "One" }, { value: 2, label: "Two" }, { value: 3, label: "Three" }, { value: 4, label: "Four" }]}
						onChange={value => this.update({ signatoryCountDraft: value })}
						placeholder="How many sign…"
					/>
				</div>

				<div className="stmt-settings__field">
					<label className="tk-form-label">What they are called</label>
					<input
						className="tk-input"
						data-test="signatory-title"
						value={signatoryTitleDraft}
						placeholder="Director"
						onChange={e => this.update({ signatoryTitleDraft: e.target.value })}
						spellCheck={true}
					/>
				</div>

				{this.renderApply({ label: "Apply signature block", testId: "apply-approval-block", onClick: this.onSaveApprovalBlock, unchanged })}
			</section>
		);
	};

	renderCompilationReportPanel = () => {
		const { compilationReportCity, compilationReportDate, compilationReportSignature } = this.props;
		const { compilationReportCityDraft, compilationReportDateDraft } = this.state;
		const signature = compilationReportSignature || {};

		const unchanged = compilationReportCityDraft.trim() === (compilationReportCity || "")
			&& compilationReportDateDraft === (compilationReportDate || "");

		return (
			<section className="stmt-settings__group">
				<p className="stmt-settings__hint">
					The compilation engagement report is signed off where the firm is, and carries the date the
					compilation was completed. Both are set here.
				</p>

				<div className="stmt-settings__field">
					<label className="tk-form-label">City the report is signed in</label>
					<input
						className="tk-input"
						data-test="compilation-report-city"
						value={compilationReportCityDraft}
						placeholder="The firm's own city"
						onChange={e => this.update({ compilationReportCityDraft: e.target.value })}
						spellCheck={true}
					/>
					<p className="tk-form-help">
						Leave blank to use the firm's own city. This is where the practitioner signed, never the client's
						address.
					</p>
					{this.renderEffectiveValue({ label: "Prints as", value: signature.city })}
				</div>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Report date</label>
					<input
						className="tk-input"
						type="date"
						data-test="compilation-report-date"
						value={compilationReportDateDraft}
						onChange={e => this.update({ compilationReportDateDraft: e.target.value })}
					/>
					<p className="tk-form-help">
						Leave blank to date the report when the engagement was finalized. It is never dated today, so
						rebuilding a package later does not change the date on a report already issued.
					</p>
					{this.renderEffectiveValue({
						label: "Prints as",
						value: signature.reportDate,
						absentNote: "Nothing yet — this engagement has not been finalized, so the report will carry no date.",
					})}
					{this.renderEffectiveValue({ label: "Signed by", value: signature.firmName })}
				</div>

				{this.renderApply({ label: "Apply to the compilation report", testId: "apply-compilation-report", onClick: this.onSaveCompilationReport, unchanged })}
			</section>
		);
	};

	renderRoundingAccount = () => {
		const { chartRows, roundingCanonicalCode } = this.props;
		const { roundingDraftCode, savingASetting } = this.state;

		const options = BookkeepingService.listBalanceSheetAccountOptions({ clientChart: chartRows });
		const unchanged = (roundingCanonicalCode || null) === (roundingDraftCode || null);
		const chosen = options.find(option => option.value === roundingCanonicalCode);

		return (
			<section className="stmt-settings__group">
				<h4 className="stmt-settings__group-title">Rounding difference</h4>
				<p className="stmt-settings__hint">
					Statements are printed to the dollar while the books are kept to the cent, so a column of rounded
					lines can end up a dollar away from its total. Choose the account that difference should appear in.
					Nothing is posted to the ledger — this changes presentation only.
				</p>

				<div className="stmt-settings__field">
					<label className="tk-form-label">Balance sheet account</label>
					<ToolsSelect
						value={roundingDraftCode}
						options={options}
						onChange={value => this.update({ roundingDraftCode: value })}
						placeholder="Choose where the rounding difference goes…"
					/>
					<p className="tk-form-help">
						{chosen
							? "Currently " + chosen.label + "."
							: "Nothing is chosen, so the statements are left exactly as they compute — a rounding difference will show rather than be absorbed."}
					</p>
				</div>

				<div className="stmt-settings__actions">
					<button
						type="button"
						className="tk-button tk-button--secondary"
						data-test="apply-rounding-account"
						onClick={this.onSaveRoundingAccount}
						disabled={savingASetting || unchanged || !roundingDraftCode}
					>
						{savingASetting ? "Applying…" : "Apply account"}
					</button>
					{roundingCanonicalCode && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={this.onClearRoundingAccount} disabled={savingASetting}>
							Clear
						</button>
					)}
				</div>
			</section>
		);
	};

	renderActivePanel = () => {
		if (this.state.activeTab === "report") return this.renderCompilationReportPanel();

		if (this.state.activeTab === "accounts") return this.renderRoundingAccount();

		return (
			<>
				{this.renderNameOnStatements()}
				{this.renderBusinessDescription()}
				{this.renderApprovalBlock()}
			</>
		);
	};

	render() {
		const { open, onClose } = this.props;

		return (
			<ToolsModal open={open} onClose={onClose} title="Statement settings" width={720}>
				<div className="stmt-settings">
					{this.state.saveFailureReason && (
						<p className="stmt-settings__save-failed">{this.state.saveFailureReason}</p>
					)}

					<ToolsTabs
						tabs={TABS}
						activeKey={this.state.activeTab}
						onSelect={key => this.update({ activeTab: key })}
					/>

					<div className="stmt-settings__panel">{this.renderActivePanel()}</div>
				</div>
			</ToolsModal>
		);
	}
}

export default StatementSettingsModal;
